import { useQueryClient } from '@tanstack/react-query';
import React from 'react';
import useGlobalStoreContext from '../../../Contexts/GlobalContext/Contexts/GlobalStoreContext/useGlobalStoreContext';
import useMultiLanguageContext from '../../MultiLanguageContext/useMultiLanguageContext';
import useSessionStoreContext from '../Contexts/SessionStoreContextProvider/useSessionStoreContext';
import useAudioServerCommunication from './useAudioServerCommunication';
import useSessionInfo from './useSessionInfo';
import useStartSession from './useStartSession';
import useStopSession from './useStopSession';

export default function useHandleSessionChange() {
  const session = useSessionInfo();
  const sessionRef = React.useRef(session);
  React.useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  const { latestDetection: [, setLatestDetection], sessionId: [, setSessionId] } = useSessionStoreContext();

  const { projectId: [projectId] } = useGlobalStoreContext();
  const projectIdRef = React.useRef(projectId);

  const { language } = useMultiLanguageContext();
  const languageRef = React.useRef(language);

  const stopSession = useStopSession();
  const startSession = useStartSession();

  const { lastJsonMessage } = useAudioServerCommunication();

  React.useEffect(() => {
    if (sessionRef.current && projectId !== projectIdRef.current && projectId && languageRef.current) {
      const { externalCallId } = sessionRef.current;
      stopSession('ProjectChanged');
      startSession({ projectId, externalCallId: externalCallId || undefined });
    }
    projectIdRef.current = projectId;
  }, [projectId, projectIdRef, sessionRef, languageRef]);

  React.useEffect(() => {
    if (sessionRef.current && language !== languageRef.current && language && projectIdRef.current) {
      const { externalCallId } = sessionRef.current;
      stopSession('LanguageChanged');
      startSession({ projectId: projectIdRef.current, externalCallId: externalCallId || undefined });
    }
    languageRef.current = language;
  }, [language, languageRef, sessionRef, projectIdRef]);

  const queryClient = useQueryClient();

  React.useEffect(() => {
    if (lastJsonMessage && lastJsonMessage.type === 'session-change') {
      queryClient.setQueryData(
        ['getLiveSession', { input: { id: lastJsonMessage.sessionId } }],
        { getSession: { ...session, id: lastJsonMessage.sessionId, detections: [] } },
      );
      queryClient.invalidateQueries(['getLiveSession', { input: { id: lastJsonMessage.sessionId } }]);

      setSessionId(lastJsonMessage.sessionId);
      if (lastJsonMessage.detection.detection) {
        setLatestDetection(lastJsonMessage.detection.detection);
      }
    }
  }, [lastJsonMessage]);
}
